import React, { useCallback, useMemo, useRef, useState } from 'react'
import ToastContainer from './container'
import { ToastContext } from './context'

export const ToastProvider = ({ children }) => {
  /** @type {[import('./types').Toast[], Function]} */
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  /**
   * @param {Omit<import('./types').Toast, 'id'> & { id?: string }} toast
   * @returns {string}
   */
  const showToast = useCallback((toast) => {
    const id = toast.id ?? `toast-${++idRef.current}`
    setToasts(prev => {
      const next = { ...toast, id }
      const existingIndex = prev.findIndex(t => t.id === id)
      if (existingIndex === -1) {
        return [...prev, next]
      }
      // Replace a toast sharing the same explicit id rather than appending a duplicate key.
      const updated = [...prev]
      updated[existingIndex] = next
      return updated
    })
    return id
  }, [])

  /** @type {import('./types').ToastApi} */
  const api = useMemo(() => ({
    showToast,
    dismissToast,
    success: (message, opts = {}) => showToast({ ...opts, message, variant: 'success' }),
    error: (message, opts = {}) => showToast({ ...opts, message, variant: 'error' }),
  }), [showToast, dismissToast])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast}/>
    </ToastContext.Provider>
  )
}
